import { Landmark, LineChart as LineChartIcon, PiggyBank, Receipt, Target, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { T } from '../theme';
import { Eyebrow, H1, Stage, Sub } from '../primitives';

const DRIVERS: [LucideIcon, string, string][] = [
  [Target, 'Budgeting & Goals', 'Define the income and priorities that matter, then build the plan to fund them.'],
  [Receipt, 'Tax Efficiency', 'Draw from the right accounts in the right order to keep taxes as low as the law allows.'],
  [Landmark, 'Benefit Timing', 'Time Social Security and pension income to maximize what you collect over your lifetime.'],
  [
    LineChartIcon,
    'Investment Returns',
    'Align each investment to its time horizon, so long-term assets grow while near-term needs stay safe.',
  ],
];

/** Act 3 — Method: equation band + the four levers. Static. */
export function ActMethod() {
  return (
    <Stage>
      <Eyebrow>How we measure success</Eyebrow>
      <H1>Four levers, one measuring tool</H1>
      <Sub>We strengthen your plan through four levers, and judge every change by a single number.</Sub>

      {/* slim conceptual equation band — quiet, sets up the levers */}
      <div
        className="deck-card"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 14,
          background: '#F4F6F8',
          border: `1px solid ${T.grayLine}`,
          borderRadius: 12,
          padding: '18px 24px',
          marginBottom: 30,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15.5, fontWeight: 600, color: T.navy }}>
          <Wallet size={18} color="var(--color-accent)" /> After-Tax Cashflow
        </span>
        <span style={{ fontSize: 18, color: T.gray, fontWeight: 300 }}>+</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15.5, fontWeight: 600, color: T.navy }}>
          <PiggyBank size={18} color="var(--color-accent)" /> Savings at End
        </span>
        <span style={{ fontSize: 18, color: T.gray, fontWeight: 300 }}>=</span>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: T.teal }}>Total Plan Value</span>
        <span style={{ fontSize: 13, color: T.gray, width: '100%', textAlign: 'center', marginTop: 5 }}>
          the total wealth your plan delivers, and the number every decision moves
        </span>
      </div>

      {/* MAIN CONTENT: the four levers, front and center */}
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: T.gray,
          marginBottom: 14,
        }}
      >
        The four levers we use to raise it
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {DRIVERS.map(([Icon, label, desc], i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 16,
              background: T.card,
              border: `1px solid ${T.grayLine}`,
              borderRadius: 12,
              padding: '22px 24px',
              boxShadow: '0 2px 8px rgba(27,42,74,.05)',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 50,
                height: 50,
                borderRadius: 12,
                background: T.tealSoft,
                color: T.teal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={25} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.teal }}>{i + 1}</span>
                <span style={{ fontWeight: 700, color: T.navy, fontSize: 16.5 }}>{label}</span>
              </div>
              <div style={{ fontSize: 14.5, color: T.ink, lineHeight: 1.55 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </Stage>
  );
}
